import { useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, type DropdownItem } from '@cyphex/ui';
import { ChevronDown, MessageSquare } from 'lucide-react';
import { QUICK_REPLIES } from '../callsAdapter';

export interface QuickReplyMenuProps {
  onReply: (message: string) => void;
  disabled?: boolean;
  /** Tells the host a menu currently owns Escape, so it must not also decline. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * The "Message ⌄" affordance at the foot of the incoming-call popup:
 * declines the call and sends the caller a canned reply.
 */
export function QuickReplyMenu({ onReply, disabled = false, onOpenChange }: QuickReplyMenuProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const items = useMemo<DropdownItem[]>(
    () =>
      QUICK_REPLIES.map((message, index) => ({
        id: `reply-${index}`,
        label: message,
        onSelect: () => onReply(message),
      })),
    [onReply],
  );

  return (
    <Dropdown
      side="top"
      align="center"
      open={open}
      onOpenChange={handleOpenChange}
      label="Reply with a message"
      className="min-w-72"
      items={items}
      trigger={
        <Button
          variant="stage"
          size="sm"
          disabled={disabled}
          leftIcon={MessageSquare}
          rightIcon={ChevronDown}
          className="bg-transparent text-popup-foreground hover:bg-white/10"
        >
          Message
        </Button>
      }
    />
  );
}
